import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Crown,
  ExternalLink,
  Heart,
  MessageCircle,
  Sparkles,
  Sprout,
  Users,
} from "lucide-react";
import {
  FOUNDERS_CHECKOUT_URL,
  FOUNDERS_PROMO_CODE,
  FOUNDERS_SEAT_COUNT,
  subscription,
} from "../mock";

const includedBenefits = [
  "Full access to the Good Fruit platform",
  "Complete Biblical Finance Course (all 8 modules)",
  "Premium Credit Mastery curriculum unlocked",
  "Partner access: IdentityIQ credit monitoring",
  "Partner access: CreditScoreIQ DIY repair toolkit",
  "Direct line to the founder for questions",
  "Early access to new features",
];

const askInReturn = [
  {
    icon: "MessageCircle",
    title: "Honest feedback",
    body: "30 minutes of your time at the 30-day mark and again at 90 days — what's working, what's broken, what's missing.",
  },
  {
    icon: "Heart",
    title: "An honest review",
    body: "If we earn it: a real testimonial we can share. If we don't: tell us why so we can fix it. No fake five stars.",
  },
  {
    icon: "Users",
    title: "Try it with your community",
    body: "Use Good Fruit with at least one ministry or small group so we can learn how it actually serves people.",
  },
];

const iconMap = { MessageCircle, Heart, Users };

const FoundersPage = () => {
  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div
          className="absolute inset-0 opacity-[0.1]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 75% 25%, #2dd4bf 0%, transparent 45%), radial-gradient(circle at 20% 70%, #14b8a6 0%, transparent 40%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-white/60 hover:text-teal-300 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Crown className="w-3.5 h-3.5" />
            <span>
              Founders&apos; Circle · {FOUNDERS_SEAT_COUNT} seats only
            </span>
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05] max-w-4xl">
            A free year of Good Fruit{" "}
            <em className="text-teal-300 font-serif font-normal">
              for 12 faith-based organizations.
            </em>
          </h1>
          <p className="mt-4 text-teal-300/80 text-sm font-serif italic max-w-2xl">
            Twelve seats — one for each of His original disciples.
          </p>
          <p className="mt-6 text-white/70 text-lg max-w-2xl leading-relaxed">
            We&apos;re inviting twelve churches, ministries, or Christian
            organizations to use Good Fruit free for one full year — full
            platform access plus Credit Mastery Premium. In return, we&apos;re
            asking for one thing:{" "}
            <strong className="text-white">your honest feedback</strong>.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={FOUNDERS_CHECKOUT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-7">
                <Sprout className="w-4 h-4 mr-2" />
                Claim a Founders&apos; seat
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#how-it-works">
              <Button
                variant="outline"
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:text-teal-300 h-12 px-6"
              >
                See how it works
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>

          <div className="mt-6 inline-flex flex-wrap items-center gap-2 text-xs text-white/55">
            <span>Promo code applied automatically:</span>
            <code className="font-mono bg-teal-400/15 border border-teal-400/30 text-teal-200 px-2 py-0.5 rounded-md">
              {FOUNDERS_PROMO_CODE}
            </code>
            <span>· No card charged for 12 months · Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="bg-white text-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
                What&apos;s included
              </div>
              <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight">
                Everything,{" "}
                <em className="text-teal-700 font-serif font-normal">
                  unlocked.
                </em>
              </h2>
              <p className="mt-5 text-black/60 leading-relaxed max-w-md">
                Full Good Fruit experience plus the {subscription.name}{" "}
                membership ({" "}
                <span className="font-semibold text-black">
                  $29.95/mo value, free for 12 months
                </span>{" "}
                ).
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-2xl bg-black text-white p-7 md:p-9 border border-black">
                <div className="inline-flex items-center gap-2 text-teal-300 text-xs uppercase tracking-wide font-medium">
                  <Sparkles className="w-3.5 h-3.5" />
                  Founders&apos; Circle benefits
                </div>
                <ul className="mt-6 space-y-3.5">
                  {includedBenefits.map((b) => (
                    <li
                      key={b}
                      className="flex items-start gap-3 text-white/85 text-base"
                    >
                      <span className="mt-0.5 w-5 h-5 rounded-full bg-teal-400 text-black flex items-center justify-center shrink-0">
                        <Check className="w-3 h-3" strokeWidth={3.5} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What we ask */}
      <section
        id="how-it-works"
        className="bg-[#04181a] text-white py-20 md:py-28 relative overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 80% 30%, #14b8a6 0%, transparent 45%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-400 font-medium">
            What we ask in return
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight max-w-3xl">
            We don&apos;t want your money.{" "}
            <em className="text-teal-300 font-serif font-normal">
              We want the truth.
            </em>
          </h2>
          <p className="mt-5 text-white/65 max-w-2xl">
            This is a real beta program — we need real feedback so we can
            build something that actually serves the body of Christ. Three
            simple asks:
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {askInReturn.map((a) => {
              const Icon = iconMap[a.icon];
              return (
                <div
                  key={a.title}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-teal-400/40 transition-colors"
                >
                  <div className="w-11 h-11 rounded-xl bg-teal-500/10 border border-teal-400/30 text-teal-300 flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-white tracking-tight">
                    {a.title}
                  </h3>
                  <p className="mt-3 text-white/65 leading-relaxed text-sm">
                    {a.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white text-black py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            How to claim a seat
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            Four steps,{" "}
            <em className="text-teal-700 font-serif font-normal">five minutes.</em>
          </h2>

          <div className="mt-12 space-y-4">
            {[
              {
                step: "01",
                title: "Click the claim button",
                body: `It opens our secure Stripe checkout with the ${FOUNDERS_PROMO_CODE} promo code already applied.`,
              },
              {
                step: "02",
                title: "Enter your card (no charge)",
                body: "Stripe asks for a card on file but $0.00 is charged for the next 12 months. Card stays on file in case you choose to continue after the year ends.",
              },
              {
                step: "03",
                title: "We send your welcome email",
                body: "Within 24 hours you'll get a personal welcome from Min. Tyree with login info and the feedback survey schedule.",
              },
              {
                step: "04",
                title: "You build, we listen",
                body: "Use Good Fruit with your community. We'll check in at 30 and 90 days. After 12 months, choose to renew, convert to paid, or cancel — no pressure.",
              },
            ].map((s) => (
              <div
                key={s.step}
                className="rounded-2xl border border-black/10 bg-white p-7 hover:border-teal-600/40 transition-colors"
              >
                <div className="flex items-start gap-5">
                  <div className="text-4xl font-semibold text-teal-700 tracking-tight shrink-0 w-14">
                    {s.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-black tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-black/60 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-3xl bg-[#04181a] border border-teal-400/30 p-10 md:p-14 overflow-hidden text-center">
            <div
              className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-30"
              style={{
                background:
                  "radial-gradient(circle, #14b8a6 0%, transparent 70%)",
              }}
            />
            <div className="relative">
              <div className="text-xs uppercase tracking-[0.2em] text-teal-300 font-medium">
                {FOUNDERS_SEAT_COUNT} seats · First come, first served
              </div>
              <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight max-w-2xl mx-auto">
                Help us build{" "}
                <em className="text-teal-300 font-serif font-normal">
                  good fruit.
                </em>
              </h2>
              <p className="mt-5 text-white/65 max-w-xl mx-auto">
                One year of full Good Fruit + Credit Mastery, free. In
                exchange for honesty, not money.
              </p>
              <a
                href={FOUNDERS_CHECKOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8"
              >
                <Button className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-7">
                  Claim a Founders&apos; seat
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <p className="mt-4 text-xs text-white/45">
                Promo code{" "}
                <span className="font-mono text-teal-300">
                  {FOUNDERS_PROMO_CODE}
                </span>{" "}
                · Free for 12 months · No charge today
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default FoundersPage;
